import { useEffect, useId, useState, useRef } from "react";
import { flushSync } from "react-dom";
import dayjs from 'dayjs';
import 'dayjs/locale/hr';
import { MergeWithOverwrite, ValidationErrorStyle } from "src/common";
import { errorIdFor, focusRing } from "src/common/style/a11y.style";
import { InputLabel } from "src/components";
import styled from "@emotion/styled";
import { colors } from "src/theme";

// Configure dayjs to use Croatian locale for month names
dayjs.locale('hr');

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

export type InputDateRangePickerProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  startLabel?: string;
  endLabel?: string;
  value?: DateRangeValue;
  startError?: string;
  endError?: string;
  canBeLargeThanCurrentYear?: boolean;
  onChange?: (value: DateRangeValue) => void;
  onStartDateChange?: (value: string) => void;
  onEndDateChange?: (value: string) => void;
  showEndDate?: boolean;
  endDateDisabled?: boolean;
  placeholder?: string;
}>;

// Internal single date picker component
interface SingleDatePickerProps {
  value: string;
  label?: string;
  error?: string;
  minDate?: any;
  maxDate?: any;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  isStartDate?: boolean;
  otherDateValue?: string; // For end date, this is start date; for start date, this is end date
  syncMonth?: any; // Month to sync to (for end date picker to sync with start date)
}

const CalendarContainer = styled.div({
  position: 'relative',
  display: 'inline-block',
  width: '100%',
  maxWidth: '600px'
});

const CalendarWrapper = styled.div<{ isSingleDate?: boolean }>((props) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: 'white',
  border: `1px solid ${colors.border5}`,
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  padding: '16px',
  marginTop: '4px',
  minWidth: props.isSingleDate ? '320px' : '600px',
  width: 'max-content',

  '@media (max-width: 768px)': {
    position: 'fixed',
    top: '50%',
    left: '50%',
    right: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: props.isSingleDate ? '350px' : '400px',
    minWidth: 'auto',
    maxHeight: '80vh',
    overflow: 'auto'
  }
}));


const MonthCalendar = styled.div<{ isSingleDate?: boolean }>((props) => ({
  minWidth: props.isSingleDate ? '320px' : '280px',
  width: props.isSingleDate ? '320px' : '280px',

  '@media (max-width: 768px)': {
    minWidth: '100%',
    width: '100%',
    maxWidth: props.isSingleDate ? '350px' : '320px'
  }
}));

const MonthHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px'
});

const MonthTitle = styled.h3({
  margin: 0,
  fontSize: '16px',
  fontWeight: 600,
  color: colors.text3
});

const NavButton = styled.button({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  color: colors.text3,
  fontSize: '18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  transition: 'background-color 0.2s',

  '&:hover': {
    backgroundColor: colors.background5
  },

  '&:focus-visible': focusRing,

  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
});

const DayHeader = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '4px',
  marginBottom: '8px'
});

const DayHeaderCell = styled.div({
  textAlign: 'center',
  fontSize: '12px',
  fontWeight: 600,
  color: colors.text2,
  padding: '8px 4px'
});

const DayGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '0px'
});

const DayButton = styled.button<{
  isSelected?: boolean;
  isInRange?: boolean;
  isStartDate?: boolean;
  isEndDate?: boolean;
  isOtherMonth?: boolean;
  isToday?: boolean;
  isDisabled?: boolean;
}>((props) => ({
  background: 'none',
  border: 'none',
  cursor: props.isDisabled ? 'not-allowed' : 'pointer',
  padding: '8px 4px',
  borderRadius: props.isStartDate
    ? '4px 0px 0px 4px'
    : props.isEndDate
      ? '0px 4px 4px 0px'
      : '0px',
  fontSize: '14px',
  fontWeight: 400,
  opacity: props.isDisabled ? 0.3 : 1,
  backgroundColor: props.isSelected || props.isStartDate || props.isEndDate
    ? colors.primary1
    : props.isInRange
      ? colors.primary4
      : 'transparent',
  color: props.isSelected || props.isStartDate || props.isEndDate
    ? 'white'
    : props.isInRange
      ? colors.primary1
      : props.isOtherMonth
        ? colors.text4
        : props.isDisabled
          ? colors.text4
          : colors.text3,
  transition: 'all 0.2s',
  position: 'relative',

  '&:hover': {
    backgroundColor: props.isDisabled
      ? 'transparent'
      : props.isSelected || props.isStartDate || props.isEndDate
        ? colors.primary1
        : props.isInRange
          ? colors.primary4
          : colors.background5
  },

  '&:focus-visible': {
    ...focusRing,
    zIndex: 1,
  },

  ...(props.isToday && {
    fontWeight: 600,
    color: props.isSelected || props.isStartDate || props.isEndDate ? 'white' : colors.primary1
  })
}));

const InputContainer = styled.div({
  display: 'flex',
  gap: '16px',
  width: '100%',

  '@media (max-width: 480px)': {
    flexDirection: 'column',
    gap: '20px'
  }
});

const DateInput = styled.input({
  flex: 1,
  minWidth: 0,
  padding: '10px 16px',
  fontSize: '16px',
  border: `1px solid ${colors.border5}`,
  borderRadius: '8px',
  outline: 'none',
  backgroundColor: colors.background7,
  color: colors.text3,
  fontFamily: 'Red Hat Display',

  '&:focus': {
    borderColor: colors.primary1
  },

  '&:focus-visible': focusRing,

  '&::placeholder': {
    color: colors.text4
  }
});

const SingleDatePickerContainer = styled.div({
  position: 'relative',
  display: 'inline-block',
  width: '100%',
  flex: 1
});

// Internal Single Date Picker Component
const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  value,
  label,
  error,
  minDate,
  maxDate,
  onChange,
  onFocus,
  placeholder = "Select date",
  id,
  disabled,
  isStartDate = true,
  otherDateValue,
  syncMonth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<any | null>(
    value ? dayjs(value) : null
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Skips reopening when focus is returned to the input after closing
  const skipOpenOnFocus = useRef(false);
  const dayGridRef = useRef<HTMLDivElement>(null);
  // Date that should receive keyboard focus inside the calendar grid
  const [focusedDate, setFocusedDate] = useState<any | null>(null);
  const today = dayjs();
  const calendarId = `${id}-calendar`;
  const errorId = errorIdFor(id);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update selected date when value prop changes
  useEffect(() => {
    setSelectedDate(value ? dayjs(value) : null);
  }, [value]);

  // Update current month when value or syncMonth changes
  // Priority: syncMonth (when start date changes) > value (when end date is selected)
  // syncMonth only takes effect when it changes (i.e., when start date is selected)
  useEffect(() => {
    if (syncMonth && dayjs(syncMonth).isValid()) {
      // If syncMonth is provided, use it (happens when start date is selected)
      setCurrentMonth(dayjs(syncMonth));
    } else if (value && dayjs(value).isValid()) {
      // Otherwise, use the end date's month if it exists
      setCurrentMonth(dayjs(value));
    }
  }, [value, syncMonth]);

  const formatDisplayValue = (date: string): string => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  const isDateValid = (date: any): boolean => {
    // Always allow dates from today onwards
    if (date.isBefore(today.subtract(1, 'day'))) return false;

    // Check min date constraint
    if (minDate && date.isBefore(minDate.subtract(1, 'day'))) return false;

    // Check max date constraint
    if (maxDate && date.isAfter(maxDate, 'day')) return false;

    // For end date, must not be before start date (same day allowed for one-day trips)
    if (!isStartDate && otherDateValue) {
      const startDate = dayjs(otherDateValue);
      if (date.isBefore(startDate, 'day')) return false;
    }

    return true;
  };

  const closeCalendar = (returnFocus = false) => {
    setIsOpen(false);
    setFocusedDate(null);
    if (returnFocus) {
      skipOpenOnFocus.current = true;
      inputRef.current?.focus();
      skipOpenOnFocus.current = false;
    }
  };

  // Opens the calendar; keyboard focus moves into the day grid only when requested
  const openCalendar = (focusGrid = false) => {
    const initialDate = selectedDate && selectedDate.isValid() ? selectedDate : (minDate && minDate.isAfter(today) ? minDate : today);
    setCurrentMonth(initialDate);
    setFocusedDate(focusGrid ? initialDate : null);
    setIsOpen(true);
    if (onFocus) onFocus();
  };

  const handleInputFocus = () => {
    if (!skipOpenOnFocus.current && !isOpen) openCalendar();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openCalendar(true);
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      closeCalendar();
    } else if (event.key === 'Tab' && !event.shiftKey && isOpen) {
      // Remove the calendar before the browser moves focus, so Tab skips it and goes to the next field
      flushSync(() => closeCalendar());
    }
  };

  // Move keyboard focus to the active day whenever it changes
  useEffect(() => {
    if (!isOpen || !focusedDate || !dayGridRef.current) return;
    const button = dayGridRef.current.querySelector<HTMLButtonElement>(`[data-date="${focusedDate.format('YYYY-MM-DD')}"]`);
    button?.focus();
  }, [isOpen, focusedDate, currentMonth]);

  const handleDateClick = (date: any) => {
    if (!isDateValid(date)) return;

    const dateStr = date.format('YYYY-MM-DD');
    setSelectedDate(date);
    
    if (onChange) {
      onChange(dateStr);
    }

    // Close picker after selecting date
    closeCalendar(true);
  };

  const handleDayKeyDown = (event: React.KeyboardEvent, date: any) => {
    const offsets: { [key: string]: number } = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

    if (event.key in offsets) {
      event.preventDefault();
      const nextDate = date.add(offsets[event.key], 'day');
      if (!nextDate.isSame(currentMonth, 'month')) {
        setCurrentMonth(nextDate);
      }
      setFocusedDate(nextDate);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleDateClick(date);
    }
  };

  const handleCalendarKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeCalendar(true);
    }
  };

  // Close the calendar when focus leaves the picker so it never covers other controls
  const handleContainerBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (isOpen && containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
      closeCalendar();
    }
  };

  const handleInputChange = (inputValue: string) => {
    const date = dayjs(inputValue, 'DD/MM/YYYY', true);
    if (date.isValid() && isDateValid(date)) {
      const dateStr = date.format('YYYY-MM-DD');
      if (onChange) {
        onChange(dateStr);
      }
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => prev.add(direction === 'next' ? 1 : -1, 'month'));
    setFocusedDate(null);
  };

  const generateCalendarDays = (month: any) => {
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    
    // Get the day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = startOfMonth.day();
    // Convert to Monday-based (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
    const mondayBasedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Start from the Monday of the week containing the first day of the month
    const startOfWeek = startOfMonth.subtract(mondayBasedDay, 'day');
    
    // Get the day of week for the last day of the month
    const lastDayOfWeek = endOfMonth.day();
    // Convert to Monday-based
    const lastMondayBasedDay = lastDayOfWeek === 0 ? 6 : lastDayOfWeek - 1;
    // End on the Sunday of the week containing the last day of the month
    const endOfWeek = endOfMonth.add(6 - lastMondayBasedDay, 'day');

    const days = [];
    let current = startOfWeek;

    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }

    return days;
  };

  const renderCalendar = () => {
    const days = generateCalendarDays(currentMonth);
    // Exactly one day is reachable with Tab; arrow keys move between days
    const tabTarget = focusedDate
      || (selectedDate && selectedDate.isSame(currentMonth, 'month') ? selectedDate : null)
      || days.find((day: any) => day.isSame(currentMonth, 'month') && isDateValid(day));

    return (
      <MonthCalendar isSingleDate={true}>
        <MonthHeader>
          <NavButton
            type="button"
            aria-label="Prethodni mjesec"
            onClick={() => navigateMonth('prev')}
            disabled={minDate && (currentMonth.isBefore(minDate, 'month') || currentMonth.isSame(minDate, 'month'))}
          >
            ‹
          </NavButton>
          <MonthTitle aria-live="polite">{currentMonth.format('MMMM YYYY')}</MonthTitle>
          <NavButton
            type="button"
            aria-label="Sljedeći mjesec"
            onClick={() => navigateMonth('next')}
            disabled={maxDate && (currentMonth.isAfter(maxDate, 'month') || currentMonth.isSame(maxDate, 'month'))}
          >
            ›
          </NavButton>
        </MonthHeader>

        <DayHeader aria-hidden="true">
          {['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'].map(day => (
            <DayHeaderCell key={day}>{day}</DayHeaderCell>
          ))}
        </DayHeader>

        <DayGrid ref={dayGridRef}>
          {days.map((day, index) => {
            const isOtherMonth = !day.isSame(currentMonth, 'month');
            const isToday = day.isSame(today, 'day');
            const isDisabled = !isDateValid(day);
            const isSelected = selectedDate ? day.isSame(selectedDate, 'day') : false;
            const isFocusTarget = tabTarget ? day.isSame(tabTarget, 'day') : false;

            return (
              <DayButton
                key={index}
                type="button"
                data-date={day.format('YYYY-MM-DD')}
                onClick={() => handleDateClick(day)}
                onKeyDown={(e) => handleDayKeyDown(e, day)}
                isOtherMonth={isOtherMonth}
                isToday={isToday}
                isDisabled={isDisabled}
                isSelected={isSelected}
                aria-disabled={isDisabled || undefined}
                aria-pressed={isSelected}
                aria-current={isToday ? 'date' : undefined}
                aria-label={day.format('dddd, D. MMMM YYYY.')}
                tabIndex={isFocusTarget ? 0 : -1}
              >
                {day.format('D')}
              </DayButton>
            );
          })}
        </DayGrid>
      </MonthCalendar>
    );
  };

  return (
    <SingleDatePickerContainer ref={containerRef} onBlur={handleContainerBlur}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {label && (
          <InputLabel htmlFor={id} disabled={disabled}>
            {label}
          </InputLabel>
        )}
        <DateInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={formatDisplayValue(value)}
          onChange={(e) => handleInputChange(e.target.value)}
          style={{ borderColor: error ? colors.error1 : undefined }}
          disabled={disabled}
          id={id}
          aria-invalid={error ? true : undefined}
          onFocus={handleInputFocus}
          onClick={() => !isOpen && openCalendar()}
          onKeyDown={handleInputKeyDown}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? calendarId : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </div>

      {isOpen && (
        <CalendarWrapper
          isSingleDate={true}
          id={calendarId}
          role="dialog"
          aria-label={label ? `${label} – kalendar` : 'Kalendar'}
          onKeyDown={handleCalendarKeyDown}
        >
          {renderCalendar()}
        </CalendarWrapper>
      )}

      {error && <ValidationErrorStyle id={errorId}>{error}</ValidationErrorStyle>}
    </SingleDatePickerContainer>
  );
};

export const InputDateRangePicker: React.FC<InputDateRangePickerProps> = ({
  label,
  startLabel,
  endLabel,
  value = { startDate: '', endDate: '' },
  startError = '',
  endError = '',
  canBeLargeThanCurrentYear = false,
  onChange,
  onStartDateChange,
  onEndDateChange,
  showEndDate = true,
  endDateDisabled = false,
  placeholder = "Select date range",
  ...props
}) => {
  const today = dayjs();
  const generatedId = useId();
  const baseId = props.id || generatedId;

  // Calculate max date for end date (360 days from start date)
  const maxEndDate = value.startDate
    ? dayjs(value.startDate).add(360, 'days')
    : today.add(360, 'days');

  const handleStartDateChange = (dateStr: string) => {
    if (onStartDateChange) onStartDateChange(dateStr);
    if (onChange) {
      onChange({
        startDate: dateStr,
        endDate: value.endDate
      });
    }
  };

  const handleEndDateChange = (dateStr: string) => {
    if (onEndDateChange) onEndDateChange(dateStr);
    if (onChange) {
      onChange({
        startDate: value.startDate,
        endDate: dateStr
      });
    }
  };

  return (
    <div>
      <CalendarContainer>
        <InputContainer>
          {/* Start Date Picker */}
          <SingleDatePicker
            value={value.startDate}
            label={label || startLabel || "Start date"}
            error={startError}
            minDate={today}
            maxDate={canBeLargeThanCurrentYear ? undefined : today.add(360, 'days')}
            onChange={handleStartDateChange}
            placeholder="DD/MM/GGGG"
            id={`${baseId}-start`}
            disabled={props.disabled}
            isStartDate={true}
          />

          {/* End Date Picker */}
          {showEndDate && (
            <SingleDatePicker
              value={value.endDate || ''}
              label={endLabel || "Zadnji dan osiguranja"}
              error={endError}
              minDate={value.startDate ? dayjs(value.startDate) : today}
              maxDate={maxEndDate}
              onChange={handleEndDateChange}
              placeholder="DD/MM/GGGG"
              id={`${baseId}-end`}
              disabled={props.disabled || endDateDisabled}
              isStartDate={false}
              otherDateValue={value.startDate}
              syncMonth={value.startDate ? dayjs(value.startDate) : undefined}
            />
          )}
        </InputContainer>

      </CalendarContainer>
    </div>
  );
};
